/**
 * COACHDIA Pulse — Native Participant Test (Iter253 Phase 3.1)
 * =============================================================
 *
 * Test Pulse autonome (aucune dépendance UX à Discovery). Le participant :
 *   1. charge la banque de questions via GET /invitations/{token}/questions
 *   2. répond aux 25 questions structurées (Likert + forced-choice) + 3 ouvertes
 *   3. soumet en un seul POST /invitations/{token}/submit
 *
 * UX premium : une question à la fois, progression or, transitions douces,
 * palette espresso/crème/or cohérente avec /team-pulse.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowRight, ChevronLeft, Check, AlertCircle, Sparkles, ShieldCheck,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

const LIKERT_LABELS = [
  'Pas du tout',
  'Très peu',
  'Peu',
  'Neutre',
  'Assez',
  'Beaucoup',
  'Totalement',
];

export default function PulseTest() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState('loading'); // loading | testing | submitting | done | error
  const [errorLabel, setErrorLabel] = useState('');
  const [meta, setMeta] = useState(null);      // { batch_name, owner_display, expires_at }
  const [structured, setStructured] = useState([]);
  const [openQs, setOpenQs] = useState([]);
  const [step, setStep] = useState(0);          // index global (structured puis open)
  const [structuredAnswers, setStructuredAnswers] = useState({}); // id -> value
  const [aiAnswers, setAiAnswers] = useState({});                 // id -> text

  const totalSteps = structured.length + openQs.length;
  const current = step < structured.length
    ? { kind: 'structured', q: structured[step] }
    : { kind: 'open', q: openQs[step - structured.length] };
  const progress = totalSteps > 0 ? Math.round(((step) / totalSteps) * 100) : 0;

  // ─── Chargement initial ───
  useEffect(() => {
    if (!token) { setErrorLabel('Lien invalide.'); setState('error'); return; }
    (async () => {
      try {
        // Start (transition not_started → in_progress). On ignore 409 (déjà in_progress)
        try {
          await axios.post(`${API}/team-pulse/invitations/${token}/start`);
        } catch (_) { /* tolère déjà in_progress / 409 */ }
        const { data } = await axios.get(`${API}/team-pulse/invitations/${token}/questions`);
        setStructured(data.structured || []);
        setOpenQs(data.open || []);
        setMeta({
          batch_name: data.batch_name,
          owner_display: data.owner_display,
          expires_at: data.expires_at,
        });
        setState('testing');
      } catch (err) {
        const s = err?.response?.status;
        if (s === 410) setErrorLabel('Ce test n\'est plus actif.');
        else if (s === 409) setErrorLabel('Vous avez déjà complété ce test. Merci !');
        else setErrorLabel('Lien invalide ou expiré.');
        setState('error');
      }
    })();
  }, [token]);

  const handleAnswerStructured = useCallback((value) => {
    const q = current.q;
    setStructuredAnswers((prev) => ({ ...prev, [q.id]: value }));
    if (step + 1 < totalSteps) {
      setTimeout(() => setStep((s) => s + 1), 220);
    }
  }, [current, step, totalSteps]);

  const handleAnswerOpen = useCallback((text) => {
    const q = current.q;
    setAiAnswers((prev) => ({ ...prev, [q.id]: text }));
  }, [current]);

  const handleNext = useCallback(() => {
    if (step + 1 < totalSteps) setStep((s) => s + 1);
  }, [step, totalSteps]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const canAdvance = useMemo(() => {
    if (!current?.q) return false;
    if (current.kind === 'structured') return structuredAnswers[current.q.id] !== undefined;
    // Open : minimum 15 chars
    const t = aiAnswers[current.q.id] || '';
    return t.trim().length >= 15;
  }, [current, structuredAnswers, aiAnswers]);

  // ─── Soumission finale ───
  const handleSubmit = useCallback(async () => {
    setState('submitting');
    try {
      const structured_answers = structured.map((q) => ({
        question_id: q.id,
        // raw accepte Likert 1-7 numérique OU l'id de l'option pour forced-choice
        raw: structuredAnswers[q.id],
      }));
      const ai_answers = openQs.map((q) => ({
        question_id: q.id,
        text: (aiAnswers[q.id] || '').trim(),
      }));
      await axios.post(`${API}/team-pulse/invitations/${token}/submit`, {
        structured_answers,
        ai_answers,
      });
      setState('done');
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = (typeof raw === 'string' && /[éèêàâîôûçùœ]/i.test(raw))
        ? raw : 'Envoi impossible pour le moment — réessayez dans quelques instants.';
      toast.error(msg);
      setState('testing');
    }
  }, [structured, openQs, structuredAnswers, aiAnswers, token]);

  // ─── Rendu états non-test ───
  if (state === 'loading' || state === 'submitting') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center" data-testid="pulse-test-loading">
        <div className="text-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#92710A] mx-auto" />
          <p className="mt-4 text-sm text-[#4A3F2F]">
            {state === 'submitting' ? 'Envoi de vos réponses…' : 'Préparation de votre test…'}
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center p-6" data-testid="pulse-test-error">
        <div className="max-w-md bg-white border border-[#EDE4D0] rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-stone-100 text-stone-500 mb-4">
            <AlertCircle className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-serif text-[#1F1A12] mb-2">{errorLabel}</h1>
          <p className="text-sm text-[#4A3F2F]">
            Revenez vers la personne qui vous a invité pour renouveler l'accès.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center p-6" data-testid="pulse-test-done">
        <div className="max-w-md bg-white border border-[#EDE4D0] rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#1F1A12] text-[#D4AF37] mb-5">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mx-auto mb-4" />
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium mb-2">
            <Sparkles className="inline h-3 w-3 mr-1" /> Merci de votre contribution
          </div>
          <h1 className="text-2xl font-serif text-[#1F1A12] mb-3">Test complété.</h1>
          <p className="text-sm text-[#4A3F2F] leading-relaxed mb-6">
            Vos réponses viennent d'enrichir le rapport d'équipe de <strong>{meta?.batch_name}</strong>.
            Seule la personne qui vous a invité y aura accès — en toute confidentialité.
          </p>
          <a
            href="/"
            className="block w-full bg-[#1F1A12] text-white px-6 py-3.5 rounded-full font-medium text-sm hover:bg-[#2A2319] transition"
            data-testid="pulse-test-discover-btn"
          >
            Découvrir COACHDIA
          </a>
          <button
            onClick={() => { try { window.close(); } catch (_) { navigate('/'); } }}
            className="mt-3 w-full text-xs text-[#8A7A5E] hover:text-[#1F1A12] transition"
            data-testid="pulse-test-close-btn"
          >
            Fermer cet onglet
          </button>
        </div>
      </div>
    );
  }

  // ─── Écran test ───
  const q = current?.q;
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="pulse-test-screen">
      {/* Header progression sticky */}
      <div className="sticky top-0 z-30 bg-[#F5F1EA]/95 backdrop-blur border-b border-[#EDE4D0]">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between text-xs text-[#8A7A5E] mb-2">
            <span className="uppercase tracking-[0.2em]">
              COACHDIA Pulse · Question {Math.min(step + 1, totalSteps)}/{totalSteps}
            </span>
            <span className="tabular-nums" data-testid="pulse-test-progress">
              {progress}%
            </span>
          </div>
          <div className="h-1.5 bg-[#EDE4D0] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#92710A] to-[#D4AF37] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question body */}
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium">
            {current.kind === 'structured' ? (q.category || 'Contexte') : 'Ouvert · 15 car. min.'}
          </div>
          <h2
            className="mt-3 text-3xl md:text-4xl font-serif text-[#1F1A12] leading-[1.2]"
            data-testid="pulse-test-question-text"
          >
            {q.text}
          </h2>
        </div>

        {current.kind === 'structured' && q.type !== 'forced_choice' && (
          <div data-testid="pulse-test-likert-grid">
            <div className="grid grid-cols-7 gap-1.5 md:gap-3">
              {LIKERT_LABELS.map((label, i) => {
                const value = i + 1;
                const selected = structuredAnswers[q.id] === value;
                return (
                  <button
                    key={value}
                    onClick={() => handleAnswerStructured(value)}
                    className={`aspect-square md:aspect-auto md:py-4 rounded-xl border transition-all flex flex-col items-center justify-center ${
                      selected
                        ? 'bg-[#1F1A12] text-white border-[#1F1A12] shadow-md scale-[1.04]'
                        : 'bg-white border-[#EDE4D0] text-[#4A3F2F] hover:border-[#92710A]'
                    }`}
                    data-testid={`pulse-test-likert-${value}`}
                    aria-label={`${value} · ${label}`}
                  >
                    <div className="font-serif text-lg md:text-xl leading-none">{value}</div>
                    {/* Labels visibles uniquement md+ (mobile = legend sous grid) */}
                    <div className="hidden md:block text-[10px] leading-tight uppercase tracking-wider mt-1 px-1">{label}</div>
                  </button>
                );
              })}
            </div>
            {/* Mobile legend — extrêmes uniquement, propre et sans débordement */}
            <div className="md:hidden mt-3 flex items-center justify-between text-[10px] uppercase tracking-wider text-[#8A7A5E] px-1">
              <span>Pas du tout</span>
              <span>Totalement</span>
            </div>
          </div>
        )}

        {current.kind === 'structured' && q.type === 'forced_choice' && (
          <div className="grid gap-3" data-testid="pulse-test-forced-grid">
            {(q.options || []).map((opt) => {
              const selected = structuredAnswers[q.id] === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => handleAnswerStructured(opt.id)}
                  className={`text-left px-6 py-5 rounded-xl border transition-all ${
                    selected
                      ? 'bg-[#1F1A12] text-white border-[#1F1A12] shadow-md'
                      : 'bg-white border-[#EDE4D0] text-[#1F1A12] hover:border-[#92710A]'
                  }`}
                  data-testid={`pulse-test-forced-${opt.id}`}
                >
                  <span className="text-sm leading-relaxed">{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {current.kind === 'open' && (
          <div>
            <textarea
              value={aiAnswers[q.id] || ''}
              onChange={(e) => handleAnswerOpen(e.target.value)}
              placeholder="Exprimez-vous librement — 2 à 3 phrases suffisent."
              rows={6}
              className="w-full px-5 py-4 bg-white border border-[#EDE4D0] rounded-xl
                         focus:outline-none focus:border-[#92710A] text-[#1F1A12] text-base leading-relaxed"
              data-testid="pulse-test-open-textarea"
              maxLength={1500}
            />
            <div className="flex items-center justify-between mt-2 text-xs text-[#8A7A5E]">
              <span>Vos réponses sont confidentielles — jamais citées nominativement.</span>
              <span className="tabular-nums">
                {(aiAnswers[q.id] || '').length}/1500
              </span>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="mt-12 flex items-center justify-between gap-4">
          <button
            onClick={handleBack}
            disabled={step === 0}
            className="text-sm text-[#92710A] hover:text-[#1F1A12] inline-flex items-center gap-1.5 disabled:opacity-30"
            data-testid="pulse-test-back-btn"
          >
            <ChevronLeft className="h-4 w-4" /> Précédente
          </button>

          {step + 1 < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={!canAdvance}
              className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-[#2A2319] transition disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="pulse-test-next-btn"
            >
              Suivante <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canAdvance}
              className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-[#2A2319] transition disabled:opacity-30"
              data-testid="pulse-test-submit-btn"
            >
              Terminer mon test <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
